import { Box, Button } from '@mui/material';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getStaticContent } from '/@/components/refuge/static-content';
import { ZoneDisplay } from '/@/components/zone-display';
import settings from '/@/core/settings';
import { useAllCatalogProducts, useCornTechnologies, useSoyTechnologies } from '/@/hooks/catalog';
import { useZones } from '/@/hooks/zones';
import { _ } from '/@/locale';
import { clean, deleteTechnology, setTechnology } from '/@/store/catalog/actions';
import { useCatalogConfig, useCatalogZones } from '/@/store/catalog/apiProviders';
import { getCatalogBrand, getCatalogZonesByBrand, getSelectedTechnology } from '/@/store/catalog/selectors';
import { setSelectedFeature } from '/@/store/nav/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import CatalogHeader from '/@/v3/catalog/catalog-header';
import TechnologyList from '/@/v3/catalog/technology-list';
import Title from '/@/v3/components/title';
import CatalogAction from '../catalog-action';
import { StaticSoyBiotechnology } from './static';
import styles from './styles.module.css';
import Vtpro4Warning from './vtpro4-warning';

@connect(
    (state, props) => {
        return {
            brand: getCatalogBrand(state, props.type),
            country: getSessionCountry(state),
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                setTechnology,
                deleteTechnology,
                clean,
                setSelectedFeature,
            },
            dispatch
        )
)
@autobind
class Biotechnology extends Component {
    static propTypes = {
        brand: PropTypes.object,
        country: PropTypes.object,
    };

    static contextTypes = {
        history: PropTypes.object,
    };

    UNSAFE_componentWillMount() {
        if (this.props.config && !this.props.config.enableTechnologyCatalog) {
            return this.context.history(
                reverse('catalog.ProductSelection', {
                    params: {
                        type: this.props.type,
                        slug: this.props.brand.slug,
                    },
                })
            );
        }
    }

    renderHeader() {
        const isStatic = this.props.config && this.props.config.technologyCatalogDataSource === 'static';
        const brand = isStatic
            ? {
                  ...this.props.brand,
                  logoCodename: this.props.type === 'soy' ? 'intacta' : this.props.brand.logoCodename,
              }
            : this.props.brand;

        return (
            <CatalogHeader
                brand={brand}
                onGoBack={this.handleGoBack}
                biotechnology
                title={
                    isStatic
                        ? _(`Catálogo de biotecnología de ${this.props.type === 'soy' ? 'soja' : 'maíz'}`)
                        : _('Catálogo de biotecnología')
                }
                subtitle={_('La tecnología más competitiva del mercado.')}
                description={_(
                    'Presentamos las mejores tecnologías que protegen tus cultivos, minimizan tus riesgos y maximizan tu rinde.'
                )}
            />
        );
    }

    renderTitle() {
        return (
            <div className={styles.biotechnologies}>
                <div className={styles.title}>{_('La tecnología más competitiva del mercado.')}</div>
                <div className={styles.subtitle}>
                    {_(
                        'Presentamos las mejores tecnologías que protegen tus cultivos, minimizan tus riesgos y maximizan tu rinde.'
                    )}
                </div>
            </div>
        );
    }

    renderTechnology(technology) {
        if (!technology) return null;
        const { id, name, description } = technology;
        const showWarning =
            Boolean(this.props.technology) &&
            (name === 'VTPRO4' || name === 'VTPRO4-RIB') &&
            this.props.country.code === 'AR';
        return (
            <div key={id}>
                <div className={styles.container}>
                    <div>
                        {isMobile() && <div className={styles.name}>{_(name)}</div>}
                        <div
                            className={classNames(styles.image)}
                            style={{ backgroundImage: `url('${technology.logoImage}')` }}
                        />
                    </div>
                    <div className={styles.description}>
                        <ReactMarkdown>{_(description)}</ReactMarkdown>
                    </div>
                    {showWarning && <Vtpro4Warning />}
                    {technology && technology.profiles.length > 0 && this.renderItems(technology)}
                </div>
            </div>
        );
    }

    handleGoBack() {
        this.props.clean();
        this.context.history(
            reverse('catalog.ProductSelection', {
                params: {
                    type: this.props.type,
                    slug: this.props.brand.slug,
                },
            })
        );
    }

    handleClickCatalogAction(type) {
        const data = { view: 'Home', selectedFeature: 'recommendation', crop: type };
        this.props.setSelectedFeature(data);
        this.context.history(reverse(data.view));
    }

    componentWillUnmount() {
        this.props.deleteTechnology();
    }

    handleItemSelection(itemId, zoneId) {
        return () => {
            this.props.clean();
            this.context.history(
                reverse('catalog.ProductDetail', {
                    params: {
                        type: this.props.type,
                        itemId,
                        slug: this.props.brand.slug,
                        zoneId,
                    },
                })
            );
        };
    }

    renderRefuge() {
        const { type, country } = this.props;
        const intro = getStaticContent('intro-catalog', type, country.code);
        const structuredLayout = getStaticContent('layout-options', type, country.code);
        const ribLayout = getStaticContent('rib-layout-options', type, country.code);

        return (
            <div className={styles.refuge}>
                <div className={styles.introduction}>
                    <Box sx={{ width: '100%', backgroundColor: (t) => t.palette.primary[100], display: 'flex' }}>
                        <div className={styles.title}>
                            <Title size={'xxl'} theme={settings.app.theme}>
                                {_('Refugio y cuidado \n de las tecnologías')}
                            </Title>
                        </div>
                    </Box>

                    <Box sx={{ width: '100%', backgroundColor: (t) => t.palette.primary.main }}>
                        <div className={styles.content}>{intro && <div>{intro.render(country.code)}</div>}</div>
                    </Box>
                </div>
                <div className={styles.structuredLayoutSection}>
                    {structuredLayout && (
                        <div className={styles.structuredLayout}>
                            <Title theme={settings.app.theme} size='lg' bold>
                                {_('Refugio estructurado')}
                            </Title>
                            {structuredLayout.render(country.code)}
                        </div>
                    )}
                </div>
                <div className={styles.ribLayoutSection}>
                    {ribLayout && (
                        <div className={styles.RibLayout}>
                            <Title theme={settings.app.theme} size='lg' bold>
                                {_('Refugio en bolsa (RIB)')}
                            </Title>
                            {ribLayout.render(country.code)}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderRibZones() {
        if (!this.props.ribZones.length) return null;
        // // TO-DO: Update zone codenames to include crop so the filter below can be more dynamic.
        if (this.props.type !== 'corn') return null;
        const zones = this.props.ribZones.filter(({ codename }) => !codename.includes('NO-RIB'));

        return (
            <div className={styles.backgroundRibZones}>
                <div className={styles.ribZones}>
                    <div className={styles.title}>
                        <Title theme={settings.app.theme} size='lg' bold>
                            {_('Recomendación de refugio (RIB)')}
                        </Title>
                    </div>
                    <div className={styles.row}>
                        {zones.map((zone) => (
                            <div
                                key={zone.id}
                                className={styles.zone}
                                style={{ width: `${Math.floor(100 / zones.length)}%` }}
                            >
                                <ZoneDisplay codename={zone.codename} name={zone.name} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderActions() {
        const { type, brand } = this.props;
        const showCatalogAction = type === 'corn' || type === 'soy';
        if (!showCatalogAction) return null;
        return (
            <Box
                sx={{
                    position: 'fixed',
                    right: 0,
                    bottom: { xs: 0, md: 3 },
                }}
            >
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <CatalogAction type={type} onClick={() => this.handleClickCatalogAction(brand.type)} />
                </Box>
                <Box sx={{ display: { md: 'none' }, p: 2, backgroundColor: 'white', background: 'white' }}>
                    <Button size='small' variant='contained' onClick={() => this.handleClickCatalogAction(brand.type)}>
                        {_('Elegir el mejor producto para mi lote y ambiente')}
                    </Button>
                </Box>
            </Box>
        );
    }

    renderStatic() {
        return (
            <div className={styles.static}>
                {this.props.type === 'soy' && (
                    <StaticSoyBiotechnology
                        brand={this.props.brand}
                        country={this.props.country}
                        profiles={this.props.products}
                        zones={this.props.zones}
                        technologies={this.props.technologies}
                        enableComparison={
                            this.props.config && this.props.config.enableTechnologyCatalogProductComparison
                        }
                    />
                )}
            </div>
        );
    }

    displayTechnologyName(selected, logoImage) {
        return (
            <div>
                <div
                    className={classNames(styles.technologyName, selected ? styles.hide : '')}
                    style={{ backgroundImage: `url('${logoImage}')` }}
                />
                {selected ? <div className={styles.claim}>{this.props.technology.claim}</div> : ''}
            </div>
        );
    }

    setTechnology(technology) {
        this.props.technology && this.props.technology.id === technology
            ? this.props.deleteTechnology()
            : this.props.setTechnology(technology);
    }

    renderTechnoloyList() {
        if (this.props.type === 'soy') return null;
        return (
            <div>
                <TechnologyList
                    type={this.props.type}
                    technologies={this.props.technologies}
                    brand={this.props.brand}
                    displayTechnologyName={this.displayTechnologyName}
                    setTechnology={this.setTechnology}
                />
            </div>
        );
    }

    renderItems(technology) {
        const profiles = technology.profiles
            .map((profileId) => this.props.products.find((cp) => cp.id === profileId))
            .filter((profile) => this.props.brand.id === profile?.brand?.id);

        const catalogZones = this.props.zones.filter(
            (zone) => zone.countryId === this.props.country.id && zone.crop === this.props.type
        );

        return (
            <div>
                <div className={styles.hybridsTitle}>{_('Híbridos con esta tecnología disponible:')}</div>
                <div className={styles.hybrids}>
                    {catalogZones.map((catalogZone) => {
                        const currentCatalogZonesProfiles = profiles.filter(
                            (profile) => profile.catalogZoneId === catalogZone.id
                        );
                        if (currentCatalogZonesProfiles.length === 0) return null;
                        return (
                            <div key={catalogZone.id} className={styles.zoneCard}>
                                <div>
                                    <p>Zona {_(catalogZone.name)}</p>
                                    {currentCatalogZonesProfiles.map((profile) => (
                                        <Button
                                            variant='text'
                                            onClick={this.handleItemSelection(profile.id, catalogZone.id)}
                                            key={profile.id}
                                            sx={{ lineHeight: '150%' }}
                                        >
                                            {profile.catalogName}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        return (
            <div className={styles.root}>
                {this.renderHeader()}
                {this.renderTechnoloyList()}
                {this.props.config && this.props.config.technologyCatalogDataSource === 'static' && (
                    <div>{this.renderStatic()}</div>
                )}

                {this.props.config && this.props.config.technologyCatalogDataSource === 'dynamic' && (
                    <div>
                        <div className={styles.technologies}>{this.renderTechnology(this.props.technology)}</div>
                        {this.renderRefuge()}
                        {this.renderRibZones()}
                        {this.renderActions()}
                    </div>
                )}
            </div>
        );
    }
}

const BiotechnologyWrapper = (props: { type: string }) => {
    const sessionCountry = useSelector(getSessionCountry);
    const brand = useSelector((state) => getCatalogBrand(state, props.type));

    const ribZonesQuery = useZones({
        type: 'rib',
    });

    const filteredZones = useMemo(() => {
        if (ribZonesQuery.data) return ribZonesQuery.data.filter(({ country }) => country === sessionCountry.code);
        return [];
    }, [ribZonesQuery.data, sessionCountry]);

    const productsQuery = useAllCatalogProducts({
        country: sessionCountry.code,
        brand: brand.name,
        crop: 'corn',
    });

    const cornTechnologiesQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const soyTechnologiesQuery = useSoyTechnologies({ page: 1, pageSize: 10000 });
    const technologies = (
        (props.type === 'corn' ? cornTechnologiesQuery.data?.results : soyTechnologiesQuery.data?.results) || []
    ).filter((t) => t.displayInCatalog);
    const selectedTechnology = useSelector((state) => getSelectedTechnology(state, technologies));

    const catalogZonesQuery = useCatalogZones({ page: 1, pageSize: 10000, crop: props.type, type: 'catalog' });
    const catalogZones = catalogZonesQuery.data?.results || [];
    const catalogConfigQuery = useCatalogConfig({ brand: brand.id });
    const zones = useSelector((state) => getCatalogZonesByBrand(state, brand, 'catalog', catalogZones));

    return (
        <Biotechnology
            {...props}
            technology={selectedTechnology}
            technologies={technologies}
            ribZones={filteredZones}
            products={productsQuery.data?.results || []}
            zones={zones}
            config={catalogConfigQuery.data}
        />
    );
};

export default BiotechnologyWrapper;
